import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  Link,
  Stack,
  Typography,
} from '@mui/material';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import { RetailerInfoField } from './retailerInfoField';
import { RetailerStatusField } from './retailerStatusField';
import { isViewPasswordDisabledForSite } from '../../../../data/contactCenter/aurora/dropDownOptions';

const cardSx = {
  borderRadius: 3,
  backgroundColor: '#FFFFFF',
  border: '1px solid rgba(5, 5, 51, 0.1)',
  boxShadow: '0 2px 8px rgba(5, 5, 51, 0.08), 0 6px 20px rgba(5, 5, 51, 0.07)',
};

export default function AuroraRetailerOverview({
  retailer,
  reps,
  repsLoading,
  passwordTellers,
  passwordVerificationLoading,
  onViewPasswordClick,
  onInventoryReviewClick,
  translations,
  hostSearchParams,
  jurisdiction,
  formatEmail,
  formatPhoneForDisplay,
  formatPhoneHref,
  formatContactDisplayName,
}) {
  const mailingAddress = retailer?.mailingAddress;
  const viewPasswordDisabled = isViewPasswordDisabledForSite(
    hostSearchParams?.site,
  );
  const showInventoryReview = hostSearchParams?.site === 'texas';

  return (
    <Card variant='outlined' sx={cardSx}>
      <CardContent
        sx={{
          pt: { xs: 1.25, sm: 1.5 },
          px: { xs: 1.5, sm: 2 },
          pb: { xs: 1.25, sm: 1.5 },
          '&:last-child': { pb: { xs: 1.25, sm: 1.5 } },
        }}
      >
        <Typography
          variant='h5'
          sx={{
            fontFamily: 'BrightstarSans-Bold',
            color: 'var(--reach-blue)',
            mb: 1,
            lineHeight: 1.25,
          }}
        >
          {retailer.locationName}
        </Typography>
        <Grid container spacing={1}>
          <RetailerInfoField
            label={translations.retailerId || 'Retailer ID'}
            value={retailer.retailerId}
            size={{ xs: 12, sm: 6, md: 4 }}
          />
          <RetailerStatusField
            label={translations.status || 'Status'}
            retailer={retailer}
            translations={translations}
            size={{ xs: 12, sm: 6, md: 4 }}
          />
          {passwordVerificationLoading ? (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 40,
              }}
            >
              <CircularProgress
                size={18}
                sx={{ color: 'var(--canopus-violet)' }}
              />
            </Grid>
          ) : passwordTellers.length || viewPasswordDisabled ? (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 40,
              }}
            >
              <Button
                variant='outlined'
                size='small'
                onClick={onViewPasswordClick}
                disabled={viewPasswordDisabled}
                sx={{
                  fontFamily: 'BrightstarSans-Bold',
                  borderColor: '#050A30',
                  color: '#050A30',
                  '&:hover': {
                    borderColor: '#050A30',
                    backgroundColor: 'rgba(5, 10, 48, 0.04)',
                  },
                  '&.Mui-disabled': {
                    borderColor: 'rgba(5, 5, 51, 0.2)',
                    color: 'rgba(5, 5, 51, 0.38)',
                  },
                }}
              >
                {translations.viewPasswords || 'View Passwords'}
              </Button>
            </Grid>
          ) : null}
          {showInventoryReview ? (
            <Grid
              size={{ xs: 12, sm: 6, md: 4 }}
              offset={{ md: 8 }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                minHeight: 40,
              }}
            >
              <Button
                variant='outlined'
                size='small'
                onClick={onInventoryReviewClick}
                sx={{
                  fontFamily: 'BrightstarSans-Bold',
                  borderColor: '#050A30',
                  color: '#050A30',
                  '&:hover': {
                    borderColor: '#050A30',
                    backgroundColor: 'rgba(5, 10, 48, 0.04)',
                  },
                }}
              >
                {translations.inventoryReview || 'Inventory Review'}
              </Button>
            </Grid>
          ) : null}
          <RetailerInfoField
            label={translations.district || 'District'}
            value={retailer.retailerDistrictName}
            size={{ xs: 12, sm: 6, md: 4 }}
          />
          <RetailerInfoField
            label={translations.territory || 'Territory'}
            value={retailer.retailerLsrTerritoryName}
            size={{ xs: 12, sm: 6, md: 4 }}
          />
          <RetailerInfoField
            label={translations.address || 'Address'}
            value={
              [
                mailingAddress?.line1,
                mailingAddress?.city &&
                  `${mailingAddress.city}, ${mailingAddress.state} ${mailingAddress.postalCode}${mailingAddress.postalCodeExt ? `-${mailingAddress.postalCodeExt}` : ''}`,
              ]
                .filter(Boolean)
                .join(' • ') ||
              translations.addressUnavailable ||
              'Address not available'
            }
            size={{ xs: 12 }}
          />
        </Grid>

        <Typography
          variant='caption'
          color='text.secondary'
          display='block'
          sx={{
            mt: 1,
            mb: 0.5,
            textTransform: 'uppercase',
            letterSpacing: 0.4,
          }}
        >
          {translations.contacts || 'Lottery Account Contacts'}
        </Typography>

        {repsLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <CircularProgress
              size={24}
              sx={{ color: 'var(--canopus-violet)' }}
            />
          </Box>
        ) : reps.length ? (
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {reps.map((rep, index) => {
              const retailerLocation = {
                site: hostSearchParams?.site,
                jurisdiction,
              };
              const emailDisplay = formatEmail(rep.workEmail);
              const phoneDisplay = formatPhoneForDisplay(
                rep.workPhone,
                retailerLocation,
              );
              const phoneHref = formatPhoneHref(
                rep.workPhone,
                retailerLocation,
              );

              return (
                <Box
                  key={rep.representativeOid ?? `${rep.workEmail}-${index}`}
                  sx={{
                    py: 1,
                    px: 1.25,
                    borderRadius: 2,
                    border: '1px solid rgba(5, 5, 51, 0.08)',
                    backgroundColor: 'rgba(5, 10, 48, 0.03)',
                    boxShadow: '0 1px 4px rgba(5, 5, 51, 0.06)',
                    minWidth: 0,
                    textAlign: 'center',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant='body2'
                    sx={{
                      fontFamily: 'BrightstarSans-Bold',
                      color: 'var(--reach-blue)',
                      lineHeight: 1.3,
                      fontSize: '0.9375rem',
                    }}
                  >
                    {formatContactDisplayName(rep)}
                  </Typography>
                  <Typography
                    variant='caption'
                    sx={{
                      color: 'text.secondary',
                      lineHeight: 1.3,
                      fontSize: '0.75rem',
                    }}
                  >
                    {rep.repType || 'No Representative Type'}
                  </Typography>
                  {emailDisplay && (
                    <Link
                      href={`mailto:${emailDisplay}`}
                      underline='always'
                      sx={{
                        display: 'inline-flex',
                        justifyContent: 'center',
                        gap: 0.5,
                        maxWidth: '100%',
                        px: 0.25,
                        color: 'var(--vega-blue)',
                        lineHeight: 1.4,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        cursor: 'pointer',
                        wordBreak: 'break-word',
                        '&:hover': {
                          color: 'var(--reach-blue)',
                          textDecoration: 'underline',
                        },
                        alignItems: 'flex-start',
                      }}
                    >
                      <EmailOutlinedIcon
                        sx={{
                          fontSize: 15,
                          color: 'inherit',
                          mt: 0.15,
                          flexShrink: 0,
                        }}
                      />
                      {emailDisplay}
                    </Link>
                  )}
                  {phoneDisplay ? (
                    <Link
                      href={phoneHref}
                      underline='always'
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 0.5,
                        maxWidth: '100%',
                        px: 0.25,
                        color: 'var(--vega-blue)',
                        lineHeight: 1.4,
                        fontSize: '0.75rem',
                        fontWeight: 500,
                        textDecoration: 'underline',
                        textUnderlineOffset: '2px',
                        cursor: 'pointer',
                        wordBreak: 'break-word',
                        '&:hover': {
                          color: 'var(--reach-blue)',
                          textDecoration: 'underline',
                        },
                      }}
                    >
                      <PhoneOutlinedIcon
                        sx={{ fontSize: 15, color: 'inherit', flexShrink: 0 }}
                      />
                      {phoneDisplay}
                    </Link>
                  ) : (
                    <Stack
                      direction='row'
                      spacing={0.5}
                      alignItems='center'
                      justifyContent='center'
                      sx={{ maxWidth: '100%', px: 0.25 }}
                    >
                      <PhoneOutlinedIcon
                        sx={{
                          fontSize: 15,
                          color: 'text.secondary',
                          flexShrink: 0,
                        }}
                      />
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{ lineHeight: 1.4 }}
                      >
                        N/A
                      </Typography>
                    </Stack>
                  )}
                </Box>
              );
            })}
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ py: 0.25 }}>
            {translations.noContacts ||
              'No contacts available for this retailer.'}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}
